import { formatInLanguage, formatGel } from '@/utils';
import type { ApiResponse, ErrorResponse } from '@/api/handlers/responses';
import type {
    ProductDetail,
    ProductType,
    ProductUnit,
    ProductVariant,
    VariantOptionValue,
    VatType,
} from '@/api/models';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import { joinVariantOptionNames } from '../productsMeta';

/**
 * Presentational config + pure helpers for the product detail page. Render
 * (the View) and logic (the hook) both read from here so labels and formatting
 * can't drift. No data fetching lives here.
 */

const EMPTY_VALUE = '—';

/** i18n key for a product type chip (reuses the list table's type labels). */
export const PRODUCT_TYPE_LABEL_KEY: Record<ProductType, string> = {
    Simple: 'products.components.productTable.types.simple',
    Variable: 'products.components.productTable.types.variable',
};

/** i18n key per unit, under `products.detail.units.*`. */
export const PRODUCT_UNIT_LABEL_KEY: Record<ProductUnit, string> = {
    Piece: 'products.detail.units.piece',
    Kilogram: 'products.detail.units.kilogram',
    Gram: 'products.detail.units.gram',
    Liter: 'products.detail.units.liter',
    Milliliter: 'products.detail.units.milliliter',
    Meter: 'products.detail.units.meter',
    Pack: 'products.detail.units.pack',
};

/** i18n key per VAT type, under `products.detail.vat.*`. */
export const VAT_TYPE_LABEL_KEY: Record<VatType, string> = {
    Standard: 'products.detail.vat.standard',
    Reduced: 'products.detail.vat.reduced',
    Exempt: 'products.detail.vat.exempt',
};

/** Format an integer-tetri amount, or `—` when absent (BOS-162). */
export const formatMoney = (valueTetri: number | null): string =>
    valueTetri === null ? EMPTY_VALUE : formatGel(valueTetri);

/** Format a plain number (qty/measure), or `—` when absent. */
export const formatNumber = (value: number | null): string =>
    value === null ? EMPTY_VALUE : new Intl.NumberFormat().format(value);

/**
 * Format a percent value (already in percent units), or `—` when absent.
 *
 * It went through the money formatter before, which now carries a ₾ — a
 * percentage is not money and must not borrow its symbol (BOS-162).
 */
export const formatPercent = (value: number | null): string =>
    value === null
        ? EMPTY_VALUE
        : `${new Intl.NumberFormat('en-US', {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
          }).format(value)}%`;

/** Format an ISO date string to a short date, or `—` when absent. */
export const formatDate = (
    value: string | null,
    language: string | undefined,
): string =>
    // BOS-165: through `formatInLanguage`, so `MMM` is a Georgian month name
    // under ქარ rather than "Aug" on an otherwise Georgian panel.
    value ? formatInLanguage(value, 'DD MMM YYYY', language) : EMPTY_VALUE;

/** Plain `—` placeholder for optional text fields. */
export const orDash = (value: string | null): string => value ?? EMPTY_VALUE;

/**
 * A variant's option values in the product's own axis order.
 *
 * The server sorts them (bos-api BOS-132), and this sorts them again — cheap
 * insurance on a property that is invisible when it holds and reads as a bug
 * when it does not: the same product rendered "Black / L" on one row and
 * "M / Black" on the next. A value with no position keeps its place, so a
 * response from before BOS-132 is left as it arrived rather than shuffled.
 */
export const orderedVariantOptions = (
    options: readonly VariantOptionValue[],
): VariantOptionValue[] =>
    [...options].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/**
 * Human label for a variant: the option values joined with `/` (e.g.
 * `Red / M`) in the product's axis order, falling back to the server `diff`,
 * then `uniqueKey`, then the barcode. Simple products (no options) fall
 * straight through to `diff`/key. Never computed from scratch — only
 * assembled from server-provided fields.
 */
export const buildVariantLabel = (variant: ProductVariant): string => {
    if (variant.options.length > 0) {
        return joinVariantOptionNames(
            orderedVariantOptions(variant.options).map(
                (option) => option.valueName,
            ),
        );
    }
    return variant.diff ?? variant.uniqueKey ?? variant.barcode;
};

/** Whether a variant is at or below its minimum (drives the low-stock chip). */
export const isVariantLowStock = (variant: ProductVariant): boolean =>
    variant.quantity <= variant.minimumQuantity;

/** What the detail marks a variant's main barcode with (bos-app#238). */
export type VariantBarcodeMark = 'generated' | 'kept';

/**
 * "Generated" when BOS generated the main barcode, "Kept as is" when the owner
 * kept the variant's barcodes, otherwise no mark. Changing the main barcode
 * clears both flags on the API, so the refetched detail drops the mark.
 */
export const variantBarcodeMarkOf = (
    variant: Pick<ProductVariant, 'barcodeGenerated' | 'barcodeKept'>,
): VariantBarcodeMark | null => {
    if (variant.barcodeGenerated) {
        return 'generated';
    }
    return variant.barcodeKept ? 'kept' : null;
};

/** The labels that say whose stock the detail's figures count (bos-app#119). */
export type StockScopeCopyKeys = {
    /** A variant's current cost. */
    cost: string;
    /** A variant's markup. */
    markup: string;
    /** The heading over a variant's batches. */
    batches: string;
    /** The line under the Variants heading. */
    note: string;
};

/**
 * i18n keys per scope: one branch by name (`{{branch}}`), every branch, or a
 * branch the branch list hasn't named yet. Whole phrases per scope rather than
 * a name dropped into one template: Georgian inflects "all branches" and
 * "this branch" differently in each phrase.
 */
export const STOCK_SCOPE_COPY_KEYS: Record<
    StockFiguresScope['kind'],
    StockScopeCopyKeys
> = {
    branch: {
        cost: 'products.detail.stockScope.branch.cost',
        markup: 'products.detail.stockScope.branch.markup',
        batches: 'products.detail.stockScope.branch.batches',
        note: 'products.detail.stockScope.branch.note',
    },
    allBranches: {
        cost: 'products.detail.stockScope.allBranches.cost',
        markup: 'products.detail.stockScope.allBranches.markup',
        batches: 'products.detail.stockScope.allBranches.batches',
        note: 'products.detail.stockScope.allBranches.note',
    },
    unnamedBranch: {
        cost: 'products.detail.stockScope.unnamedBranch.cost',
        markup: 'products.detail.stockScope.unnamedBranch.markup',
        batches: 'products.detail.stockScope.unnamedBranch.batches',
        note: 'products.detail.stockScope.unnamedBranch.note',
    },
};

/**
 * The stock-scope labels for `scope`: their i18n keys, and the values they
 * interpolate — the branch's name, where there is one.
 */
export const stockScopeCopy = (
    scope: StockFiguresScope,
): { keys: StockScopeCopyKeys; values: { branch?: string } } => ({
    keys: STOCK_SCOPE_COPY_KEYS[scope.kind],
    values: scope.kind === 'branch' ? { branch: scope.name } : {},
});

/** bos-api's 404 for a product that doesn't exist, or no longer does (bos-app#136). */
export const PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND';

/**
 * Where the product request stands, on the details and edit pages
 * (bos-app#136). `notFound` is bos-api's `PRODUCT_NOT_FOUND` and nothing
 * else: the product was deleted, or never existed, so trying again can't
 * help. It goes by the code, not the status, because the details request has
 * another 404, `BRANCH_NOT_FOUND`, which says nothing about the product. Any
 * other failure is `error`, which offers Try again.
 */
export type ProductLoadStatus = 'loading' | 'ready' | 'notFound' | 'error';

/** A product request's status, carrying the failure to retry on `error` alone. */
export type ProductLoad =
    | { status: Exclude<ProductLoadStatus, 'error'> }
    | { status: 'error'; error: ErrorResponse };

/** Where a product request that has answered `response` so far stands. */
export const readProductLoad = (
    response: ApiResponse<ProductDetail> | undefined,
): ProductLoad => {
    if (response === undefined) {
        return { status: 'loading' };
    }
    if (response.success) {
        return { status: 'ready' };
    }
    return response.error.code === PRODUCT_NOT_FOUND
        ? { status: 'notFound' }
        : { status: 'error', error: response.error };
};
